import { useCallback, useEffect, useState } from 'react';
import { NavigateFunction, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';

import { getSelectTabsEnabled } from '@store/reducers/tabsSlice';
import { ErrorBoundary } from '@shared/components/ErrorBoundary';
import { Main, SidebarWrapper, TabOutletWrapper, Divider } from '@styles/Layout/LayoutStyles';
import { LAYOUT_GLOBAL_TABS_LIST_HEIGHT } from '@styles/StyledTabs';
import { GlobalTabs } from '@shared/tabs/GlobalTabs';
import { changeMenuVisibilityViaBtn, openedViaBtnMenu } from '@store/reducers/layoutSlice';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { useTabs } from '@shared/hooks/useTabs';
import { urlByKey, urlByKeyConfig } from '@config/routesConfig';
import { DEFAULT_ROUTES_BY_ROLES, Roles } from '@shared/constants/constants';
import { AppLoader } from './components/AppLoader';
import { Sidebar } from './components/Sidebar/Sidebar';
import { Header, LAYOUT_HEADER_HEIGHT } from './components/Header/Header';
import { IMenuItem } from './components/Sidebar/types';


export const Layout = ({ menuItems, urlsByElementKey }: { menuItems: IMenuItem[], urlsByElementKey: urlByKeyConfig}) => {
	const { pathname } = useLocation();
  const { userRoles } = useUserAccount();
  const navgiate = useNavigate();
  const dispatch = useDispatch();
  const { openTab, selectTab } = useTabs();

  const openViaButton = useSelector(openedViaBtnMenu);
  const tabsEnabled = useSelector(getSelectTabsEnabled);

  const [isOpen, setOpen] = useState(false);

  const topContentHeight = LAYOUT_HEADER_HEIGHT + (tabsEnabled ? LAYOUT_GLOBAL_TABS_LIST_HEIGHT : 10);

  const openPage = useCallback(
    (urlObj: urlByKey, navigate: NavigateFunction) => {

      if (urlObj) {
        const url = urlObj.url ?? '';
        const searchParams = urlObj?.searchParams ?? '';
        const fullUrl = url + searchParams;
        openTab({ title: urlObj?.text ?? '', route: fullUrl });

        navigate(fullUrl);
      } else {
        selectTab(-1);
      }
    },
    [menuItems]
  );

  const defaultRedirect = useCallback(
    (navigate: NavigateFunction, roles: (keyof Roles)[]) => {
      for (const role of roles) {
        const defaultRoute = urlsByElementKey[DEFAULT_ROUTES_BY_ROLES[role as Roles] as string];

        if (defaultRoute) {
          openPage(defaultRoute, navigate);
          return;
        } 
        
        if (!defaultRoute) {
          navigate('/default-role');
          return;
        }
      }
    },
    [userRoles, urlsByElementKey]
  );

  useEffect(
    () => {
      if (pathname === '/') {
        defaultRedirect(navgiate, userRoles);
      }
    },
    [pathname, userRoles]
  );

  return (
    <Box>
      <CssBaseline />

      <SidebarWrapper 
        openViaButton={openViaButton} 
        onMouseOver={() => setOpen(true)} 
        onMouseOut={() => setOpen(false)}
      >
        <Sidebar 
          open={isOpen || openViaButton}
          fetchedMenuItems={menuItems}
        />
      </SidebarWrapper>

      <Main open={isOpen} openViaButton={openViaButton}>
        <AppLoader />

        <Header open={isOpen ?? openViaButton} toggleDrawer={() => dispatch(changeMenuVisibilityViaBtn())} />

        {tabsEnabled ? <GlobalTabs /> : <Divider />}
        
        <TabOutletWrapper contentHeight={topContentHeight}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </TabOutletWrapper>
      </Main>
    </Box>
  );
};
